import React, { useEffect, useRef, useState, useCallback } from 'react';
import { IconBaseProps } from 'react-icons';
import { FiAlertCircle } from 'react-icons/fi';
import { useField } from '@unform/core';
import ReactInputMask, { Props as InputMaskProps } from 'react-input-mask';

import { Container, Error } from './styles';

interface InputProps extends InputMaskProps {
  name: string;
  title: string;
  icon?: React.ComponentType<IconBaseProps>;
}

const InputMaskAlternative: React.FC<InputProps> = ({
  name,
  icon: Icon,
  title,
  disabled,
  // Extraído do `rest` de propósito — ver `handleInputBlur`.
  onBlur,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled] = useState(false);
  const inputRef = useRef<ReactInputMask>(null);
  const { fieldName, defaultValue, error, registerField } = useField(name);

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  /**
   * ⚠️ **Compõe com o `onBlur` de quem usa o componente.** O handler interno vinha antes
   * do `{...rest}`, então um chamador que passasse `onBlur` apagava o `setIsFocused(false)`
   * em silêncio e a borda de foco ficava acesa para sempre. Achado na 0.31, ao ligar a
   * checagem de duplicidade no `blur` do CPF.
   */
  const handleInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      onBlur?.(event);
    },
    [onBlur],
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
      setValue(ref: any, value: string) {
        ref.setInputValue(value || '');
      },
      clearValue(ref: any) {
        ref.setAttribute('value', '');
      },
    });
  }, [fieldName, registerField]);

  return (
    <Container
      isErrored={!!error}
      isFilled={isFilled}
      isFocused={isFocused}
      isDisabled={!!disabled}
    >
      <strong>{title}</strong>
      {Icon && <Icon size={20} />}
      <ReactInputMask
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        defaultValue={defaultValue}
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore:next-line
        maskChar={null}
        ref={inputRef}
        disabled={disabled}
        {...rest}
      />
      {error && (
        <Error title={error}>
          <FiAlertCircle color="#c53030" size={20} />
        </Error>
      )}
    </Container>
  );
};

export default InputMaskAlternative;
